import { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowCounterclockwise } from 'react-icons/bs';

import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import TableFilterToolbar from '#globalComponents/Table/TableFilterToolbar';
import { useClampPage } from '#globalHooks/useClampPage';
import { useScopedSelection } from '#globalHooks/useScopedSelection';
import { useUrlFilters } from '#globalHooks/useUrlFilters';
import { useErrorStore } from '#lib/stores/errorStore';
import { filtersToQueryParams } from '#lib/tableFilters';

import { TRASH_FILTER_SCHEMA } from '../../utils/crmTableFilters';

import TrashTable, { TRASH_PAGE_SIZE, useRestoreHandler } from './TrashTable';

/**
 * Searchable, bulk-restorable trash tab for one entity type (companies or deals). The entity's
 * hooks and labels come from `config`, so both tabs share one implementation.
 */
const EntityTrashTab = ({ config }) => {
	const urlFilters = useUrlFilters(TRASH_FILTER_SCHEMA, { namespace: config.namespace });
	const { filters, filtersKey, page, setPage } = urlFilters;
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const queryFilters = useMemo(
		() => ({ page, pageSize: TRASH_PAGE_SIZE, ...filtersToQueryParams(filters) }),
		[page, filters],
	);
	const { data, isLoading } = config.useList(queryFilters);
	const rows = useMemo(() => data?.[config.rowsKey] ?? [], [data, config.rowsKey]);
	const total = data?.total ?? 0;
	const totalPages = data?.totalPageCount ?? 1;

	useClampPage({ page, totalPages, setPage, isReady: Boolean(data) });

	const restoreMutation = config.useRestore();
	const bulkRestoreMutation = config.useBulkRestore();
	const handleRestore = useRestoreHandler(restoreMutation, config.restoreMessage);

	const selection = useScopedSelection(rows, {
		totalCount: total,
		filters,
		resetKey: filtersKey,
		entityLabel: { singular: config.labelSingular, plural: config.labelPlural, gender: config.labelGender },
	});

	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	const handleBulkRestore = async () => {
		const { restored = 0 } = await bulkRestoreMutation.mutateAsync(selection.toActionScope());
		selection.clear();
		showSuccess({
			title: 'Wiederhergestellt',
			message: `${restored.toLocaleString('de-DE')} ${restored === 1 ? config.labelSingular : config.labelPlural} wiederhergestellt.`,
			autoClose: true,
		});
	};

	return (
		<>
			<TableFilterToolbar
				urlFilters={urlFilters}
				schema={TRASH_FILTER_SCHEMA}
				searchPlaceholder={config.searchPlaceholder}
				resultLabel={`${total.toLocaleString('de-DE')} ${config.labelPlural}`}
			/>
			<TrashTable
				rows={rows}
				columns={config.columns}
				isLoading={isLoading}
				page={page}
				totalPages={totalPages}
				total={total}
				onPageChange={setPage}
				onRestore={handleRestore}
				isRestoring={restoreMutation.isPending || bulkRestoreMutation.isPending}
				emptyLabel={filters.search ? 'Keine Treffer für diese Suche' : undefined}
				selection={selection.tableSelection}
				actions={{
					label: selection.actionScopeLabel,
					buttons: (
						<ActionScopeButton
							icon={<BsArrowCounterclockwise />}
							disabled={selection.actionCount === 0 || bulkRestoreMutation.isPending}
							onClick={() => setIsConfirmOpen(true)}
						>
							Wiederherstellen
						</ActionScopeButton>
					),
				}}
				bulkBar={
					<BulkActionsBar
						selectedCount={selection.count}
						entityLabel={selection.entityLabel}
						onClear={selection.clear}
					/>
				}
			/>
			<ConfirmModal
				isOpen={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onConfirm={handleBulkRestore}
				title="Wiederherstellen"
				message={`Möchten Sie ${selection.actionTarget} wiederherstellen?`}
				subtitle={config.restoreHint ?? null}
				confirmText="Wiederherstellen"
				confirmVariant="solid"
				isLoading={bulkRestoreMutation.isPending}
				icon={BsArrowCounterclockwise}
			/>
		</>
	);
};

EntityTrashTab.propTypes = {
	config: PropTypes.shape({
		namespace: PropTypes.string.isRequired,
		useList: PropTypes.func.isRequired,
		rowsKey: PropTypes.string.isRequired,
		columns: PropTypes.array.isRequired,
		useRestore: PropTypes.func.isRequired,
		useBulkRestore: PropTypes.func.isRequired,
		restoreMessage: PropTypes.func.isRequired,
		labelSingular: PropTypes.string.isRequired,
		labelPlural: PropTypes.string.isRequired,
		labelGender: PropTypes.oneOf(['f', 'm', 'n']).isRequired,
		searchPlaceholder: PropTypes.string.isRequired,
		/** Shown under the restore confirmation, e.g. what is restored along. */
		restoreHint: PropTypes.string,
	}).isRequired,
};

export default EntityTrashTab;
